import { FormFields } from '@App/forms/v2/common/form-field'
import { FormFieldTypes } from '@App/enum/form-field-types'
import { z } from 'zod'
import { stringSizeMaxMessage } from '@Entities/schemas/work/error-messages'
import {
    BIRTHDAY_ERROR_MESSAGE,
    EMAIL_MESSAGE,
    NO_RESERVED_EMAIL_MESSAGE,
    REQUIRED_MESSAGE,
} from '@Const/error-messages'
import {
    addressFormKey,
    userAddressFormFields,
    userAddressSchema,
    UserAddressType,
} from '@Entities/schemas/common/address-schema'
import {
    internationalPhoneNumberSchema,
    internationalPhoneNumberFormKey,
    internationalPhoneNumberFormField,
} from '@Entities/schemas/common/international-phone-number-schema'
import { settingsFormKey, settingsSchema } from '@Entities/schemas/user/user-settings'
import populateSchema from '@App/forms/v2/helpers/populate-schema'
import { isNotAnReservedEmailBoolean } from '@App/helpers/validate'
import { groupsFormKey, groupsSchema } from '@Entities/schemas/user/user-groups'

const idKey = 'id'
const firstNameKey = 'firstName'
const firstNameLabel = 'First Name'
const lastNameKey = 'lastName'
const lastNameLabel = 'Last Name'
const birthDateKey = 'birthDate'
const birthdayLabel = 'Date Of Birth'
const emailKey = 'email'
const emailLabel = 'Email Address'

const invitationSentTimestampKey = 'invitationSentTimestamp'
const activeKey = 'active'
const lastActivityKey = 'lastActivity'
const validationStatusKey = 'validationStatus'
const verificationStatusKey = 'verificationStatus'
const rolesKey = 'roles'
const isInternalKey = 'isInternal'
const usernameKey = 'username'

const userInfoBaseSchema = z.object({
    [firstNameKey]: z
        .string({
            error: REQUIRED_MESSAGE,
        })
        .min(1, REQUIRED_MESSAGE)
        .max(60, stringSizeMaxMessage(firstNameLabel, 60)),
    [lastNameKey]: z
        .string({
            error: REQUIRED_MESSAGE,
        })
        .min(1, REQUIRED_MESSAGE)
        .max(60, stringSizeMaxMessage(lastNameLabel, 60)),
    [birthDateKey]: z
        .date({
            error: 'Required',
        })
        .max(new Date(), BIRTHDAY_ERROR_MESSAGE),
    [emailKey]: z
        .string()
        .min(1, REQUIRED_MESSAGE)
        .trim()
        .toLowerCase()
        .email(EMAIL_MESSAGE)
        .max(2000, stringSizeMaxMessage(emailLabel, 2000))
        .refine(isNotAnReservedEmailBoolean, NO_RESERVED_EMAIL_MESSAGE),
    [addressFormKey]: userAddressSchema,
    [internationalPhoneNumberFormKey]: internationalPhoneNumberSchema,
})

const userInfoSchema = userInfoBaseSchema.merge(
    z.object({
        [idKey]: z.optional(z.string()),
        [settingsFormKey]: settingsSchema.optional(),
        [groupsFormKey]: groupsSchema.optional(),
        [invitationSentTimestampKey]: z.optional(z.string()).nullable(),
        [activeKey]: z.optional(z.boolean()),
        [lastActivityKey]: z.optional(z.string()).nullable(),
        [validationStatusKey]: z.optional(z.string()).nullable(),
        [verificationStatusKey]: z.optional(z.string()).nullable(),
        [rolesKey]: z.optional(z.array(z.string())),
        [isInternalKey]: z.optional(z.boolean()),
        [usernameKey]: z
            .string()
            .min(1, REQUIRED_MESSAGE)
            .trim()
            .toLowerCase()
            .email(EMAIL_MESSAGE)
            .max(2000, stringSizeMaxMessage(emailLabel, 2000))
            .refine(isNotAnReservedEmailBoolean, NO_RESERVED_EMAIL_MESSAGE),
    })
)

type UserInfoBaseType = z.infer<typeof userInfoBaseSchema>
type UserInfoType = z.infer<typeof userInfoSchema>

type UserInfoInput = z.input<typeof userInfoSchema>

const userInfoFormFieldsNoSchema: FormFields<UserInfoBaseType> = {
    [firstNameKey]: {
        name: firstNameKey,
        label: firstNameLabel,
        type: FormFieldTypes.TEXT,
    },
    [lastNameKey]: {
        name: lastNameKey,
        label: lastNameLabel,
        type: FormFieldTypes.TEXT,
    },
    [birthDateKey]: {
        name: birthDateKey,
        label: birthdayLabel,
        type: FormFieldTypes.DATEPICKER,
    },
    [emailKey]: {
        name: emailKey,
        label: emailLabel,
        type: FormFieldTypes.TEXT,
        isDisabled: true,
    },
    [addressFormKey]: userAddressFormFields as FormFields<UserAddressType>,
    [internationalPhoneNumberFormKey]: internationalPhoneNumberFormField,
}

const userInfoFormFields: FormFields<UserInfoBaseType> = populateSchema(userInfoFormFieldsNoSchema, userInfoSchema)

export {
    userInfoSchema,
    UserInfoType,
    UserInfoInput,
    userInfoFormFields,
    userInfoBaseSchema,
    internationalPhoneNumberFormKey,
}
